/**
 * Admin Service
 * Centralizes all admin panel API operations
 */

import apiService from './api';

/**
 * Get paginated users list with optional filtering
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @param {string} [params.search] - Search query
 * @param {string} [params.role] - Filter by role
 * @param {string} [params.sortBy] - Sort field
 * @param {string} [params.sortOrder] - Sort direction
 * @returns {Promise<Object>} Users list with pagination info
 */
const getUsers = async (params = {}) => {
  const searchParams = new URLSearchParams(
    Object.entries(params).filter(([_, value]) => value !== '' && value != null)
  );
  const response = await apiService.get(`/api/admin/users?${searchParams}`);
  return response.json();
};

/**
 * Get dashboard statistics
 * @returns {Promise<Object>} Dashboard stats
 */
const getDashboardStats = async () => {
  const response = await apiService.get('/api/admin/dashboard');
  return response.json();
};

/**
 * Update user profile data
 * @param {string} userId - User ID
 * @param {Object} userData - User data to update
 * @returns {Promise<Object>} Updated user data
 */
const updateUser = async (userId, userData) => {
  const response = await apiService.put(`/api/admin/users/${userId}`, userData);
  return response.json();
};

/**
 * Get user game statistics
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User game stats
 */
const getUserStats = async (userId) => {
  const response = await apiService.get(`/api/admin/users/${userId}/stats`);
  return response.json();
};

/**
 * Update user game statistics
 * @param {string} userId - User ID
 * @param {Object} statsData - Statistics data to update
 * @returns {Promise<Object>} Updated stats
 */
const updateUserStats = async (userId, statsData) => {
  const response = await apiService.put(`/api/admin/users/${userId}/stats`, statsData);
  return response.json();
};

/**
 * Get user achievements
 * @param {string} userId - User ID
 * @returns {Promise<Array>} User achievements
 */
const getUserAchievements = async (userId) => {
  const response = await apiService.get(`/api/admin/users/${userId}/achievements`);
  return response.json();
};

/**
 * Grant achievement to user
 * @param {string} userId - User ID
 * @param {string} achievementId - Achievement ID
 * @returns {Promise<Object>} Result
 */
const grantAchievement = async (userId, achievementId) => {
  const response = await apiService.post(`/api/admin/users/${userId}/achievements/grant`, { 
    achievementId 
  });
  return response.json();
};

/**
 * Revoke achievement from user
 * @param {string} userId - User ID
 * @param {string} achievementId - Achievement ID
 * @returns {Promise<Object>} Result
 */
const revokeAchievement = async (userId, achievementId) => {
  const response = await apiService.delete(`/api/admin/users/${userId}/achievements/revoke`, { 
    body: JSON.stringify({ achievementId }),
    headers: {
      'Content-Type': 'application/json'
    }
  });
  return response.json();
};

/**
 * Get feedback list with pagination
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Feedback list with pagination
 */
const getFeedback = async (params = {}) => {
  const searchParams = new URLSearchParams(
    Object.entries(params).filter(([_, value]) => value !== '' && value != null)
  );
  const response = await apiService.get(`/api/admin/feedback?${searchParams}`);
  return response.json();
};

/**
 * Get feedback statistics summary
 * @returns {Promise<Object>} Feedback stats
 */
const getFeedbackStats = async () => {
  const response = await apiService.get('/api/admin/feedback/stats/summary');
  return response.json();
};

/**
 * Update feedback status
 * @param {string} feedbackId - Feedback ID
 * @param {string} status - New status
 * @returns {Promise<Object>} Updated feedback
 */
const updateFeedbackStatus = async (feedbackId, status) => {
  const response = await apiService.put(`/api/admin/feedback/${feedbackId}`, { status });
  return response.json();
};

/**
 * Update feedback with multiple fields
 * @param {string} feedbackId - Feedback ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated feedback
 */
const updateFeedback = async (feedbackId, updates) => {
  const response = await apiService.put(`/api/admin/feedback/${feedbackId}`, updates);
  return response.json();
};

/**
 * Trigger achievement refresh for all users
 * @returns {Promise<Object>} Operation result
 */
const refreshAchievements = async () => {
  const response = await apiService.post('/api/admin/actions/refresh-achievements');
  return response.json();
};


export default {
  getUsers,
  getDashboardStats,
  updateUser,
  getUserStats,
  updateUserStats,
  getUserAchievements,
  grantAchievement,
  revokeAchievement,
  getFeedback,
  getFeedbackStats,
  updateFeedbackStatus,
  updateFeedback,
  refreshAchievements
};